import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { X } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Controller, useForm, type FieldPath } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { z } from 'zod'
import { doctorSignupService } from '../api/services/doctor-signup.service'
import { DepartmentParentPicker } from '../components/departments/DepartmentParentPicker'
import { DepartmentPicker } from '../components/departments/DepartmentPicker'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend, RequiredMark } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { MultiSelectPicker } from '../components/ui/multi-select-picker'
import { SelectPicker } from '../components/ui/select-picker'
import {
  collectDepartmentNames,
  insertDepartmentInTree,
  removeDepartmentFromTree,
} from '../lib/department-tree'
import type { ApiErrorResponse } from '../types/api'
import type { Department } from '../types/department'
import type {
  DoctorSignupFormData,
  DoctorSignupPayload,
  SignupNewDepartment,
  SignupNewHospital,
  SignupNewPosition,
} from '../types/doctor'
import { REVIEWER_SALUTATION_OPTIONS } from '../types/reviewer'

const MAX_SIGNUP_DOCUMENTS = 5
const PENDING_DEPARTMENT_ID = 'pending:department'
const PENDING_POSITION_ID = 'pending:position'

function fileKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`
}

function isPendingId(id: string): boolean {
  return id.startsWith('pending:')
}

function namesMatch(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase()
}

type PendingHospital = SignupNewHospital & { tempId: string }
type CreateModal = 'department' | 'position' | 'hospital' | null

const departmentCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  parent_id: z.string().optional(),
  is_active: z.boolean(),
})

const positionCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  is_active: z.boolean(),
})

const hospitalCreateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  address: z.string().optional(),
  is_active: z.boolean(),
})

type DepartmentCreateValues = z.infer<typeof departmentCreateSchema>
type PositionCreateValues = z.infer<typeof positionCreateSchema>
type HospitalCreateValues = z.infer<typeof hospitalCreateSchema>

const emptyDepartmentCreate: DepartmentCreateValues = {
  name: '',
  parent_id: '',
  is_active: true,
}

const emptyPositionCreate: PositionCreateValues = {
  name: '',
  is_active: true,
}

const emptyHospitalCreate: HospitalCreateValues = {
  name: '',
  address: '',
  is_active: true,
}

const signupSchema = z.object({
  salutation: z.string().min(1),
  first_name: z.string().min(1, 'First name is required'),
  surname: z.string().min(1, 'Surname is required'),
  email: z.string().email('Valid email is required'),
  phone_number: z.string().optional(),
  registration_number: z.string().min(1, 'Registration number is required'),
  department_id: z.string().min(1, 'Department is required'),
  position_id: z.string().min(1, 'Position is required'),
  hospital_ids: z.array(z.string()).min(1, 'Select at least one hospital'),
  documents: z
    .array(z.instanceof(File))
    .min(1, 'At least one document/image is required')
    .max(MAX_SIGNUP_DOCUMENTS, 'You can upload at most 5 documents/images'),
  proposer_name: z.string().min(1, 'Proposer name is required'),
  proposer_phone_number: z.string().min(1, 'Proposer phone number is required'),
  proposer_email: z.string().email('Valid proposer email is required'),
  seconder_name: z.string().min(1, 'Seconder name is required'),
  seconder_phone_number: z.string().min(1, 'Seconder phone number is required'),
  seconder_email: z.string().email('Valid seconder email is required'),
  terms_accepted: z.boolean().refine((value) => value, {
    message: 'You must accept the terms and conditions',
  }),
  privacy_accepted: z.boolean().refine((value) => value, {
    message: 'You must accept the privacy policy',
  }),
}).superRefine((values, ctx) => {
  const normalizeText = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ')
  const normalizePhone = (value: string) => value.replace(/\D+/g, '')
  const normalizeEmail = (value: string) => value.trim().toLowerCase()

  if (
    values.proposer_name &&
    values.seconder_name &&
    normalizeText(values.proposer_name) === normalizeText(values.seconder_name)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Proposer and seconder names must be different',
      path: ['seconder_name'],
    })
  }

  if (
    values.proposer_phone_number &&
    values.seconder_phone_number &&
    normalizePhone(values.proposer_phone_number) === normalizePhone(values.seconder_phone_number)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Proposer and seconder phone numbers must be different',
      path: ['seconder_phone_number'],
    })
  }

  if (
    values.proposer_email &&
    values.seconder_email &&
    normalizeEmail(values.proposer_email) === normalizeEmail(values.seconder_email)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Proposer and seconder emails must be different',
      path: ['seconder_email'],
    })
  }
})

type SignupFormValues = z.infer<typeof signupSchema>

export function DoctorSignupPage() {
  const [formData, setFormData] = useState<DoctorSignupFormData | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [createModal, setCreateModal] = useState<CreateModal>(null)
  const [departments, setDepartments] = useState<Department[]>([])
  const [pendingDepartment, setPendingDepartment] = useState<SignupNewDepartment | null>(null)
  const [pendingPosition, setPendingPosition] = useState<SignupNewPosition | null>(null)
  const [pendingHospitals, setPendingHospitals] = useState<PendingHospital[]>([])

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    mode: 'onSubmit',
    defaultValues: {
      salutation: 'dr',
      first_name: '',
      surname: '',
      email: '',
      phone_number: '',
      registration_number: '',
      department_id: '',
      position_id: '',
      hospital_ids: [],
      documents: [],
      proposer_name: '',
      proposer_phone_number: '',
      proposer_email: '',
      seconder_name: '',
      seconder_phone_number: '',
      seconder_email: '',
      terms_accepted: false,
      privacy_accepted: false,
    },
  })

  const departmentCreateForm = useForm<DepartmentCreateValues>({
    resolver: zodResolver(departmentCreateSchema),
    defaultValues: emptyDepartmentCreate,
  })

  const positionCreateForm = useForm<PositionCreateValues>({
    resolver: zodResolver(positionCreateSchema),
    defaultValues: emptyPositionCreate,
  })

  const hospitalCreateForm = useForm<HospitalCreateValues>({
    resolver: zodResolver(hospitalCreateSchema),
    defaultValues: emptyHospitalCreate,
  })

  const selectedDocuments = form.watch('documents') ?? []
  const selectedHospitalIds = form.watch('hospital_ids') ?? []

  useEffect(() => {
    void doctorSignupService.getFormData().then((response) => {
      setFormData(response.data)
      setDepartments(response.data.departments)
    })
  }, [])

  const closeCreateModal = () => setCreateModal(null)

  const openCreateModal = (modal: Exclude<CreateModal, null>) => {
    if (modal === 'department') {
      departmentCreateForm.reset(emptyDepartmentCreate)
    }
    if (modal === 'position') {
      positionCreateForm.reset(emptyPositionCreate)
    }
    if (modal === 'hospital') {
      hospitalCreateForm.reset(emptyHospitalCreate)
    }
    setCreateModal(modal)
  }

  const onSubmit = form.handleSubmit(async (values) => {
    if (!formData) {
      return
    }

    const payload: DoctorSignupPayload = {
      salutation: values.salutation,
      first_name: values.first_name,
      surname: values.surname,
      email: values.email,
      phone_number: values.phone_number,
      registration_number: values.registration_number,
      proposer_name: values.proposer_name,
      proposer_phone_number: values.proposer_phone_number,
      proposer_email: values.proposer_email,
      seconder_name: values.seconder_name,
      seconder_phone_number: values.seconder_phone_number,
      seconder_email: values.seconder_email,
      documents: values.documents,
      terms_accepted: values.terms_accepted,
      privacy_accepted: values.privacy_accepted,
      terms_version: formData.terms_version,
    }

    if (values.department_id === PENDING_DEPARTMENT_ID && pendingDepartment) {
      payload.new_department = pendingDepartment
    } else {
      payload.department_id = values.department_id
    }

    if (values.position_id === PENDING_POSITION_ID && pendingPosition) {
      payload.new_position = pendingPosition
    } else {
      payload.position_id = values.position_id
    }

    payload.hospital_ids = values.hospital_ids.filter((id) => !isPendingId(id))
    payload.new_hospitals = pendingHospitals
      .filter((hospital) => values.hospital_ids.includes(hospital.tempId))
      .map((hospital) => ({
        name: hospital.name,
        address: hospital.address,
        is_active: hospital.is_active,
      }))

    try {
      await doctorSignupService.submit(payload)
      setSubmitted(true)
    } catch (error) {
      // Keep all filled fields; surface field-level API validation (e.g. proposer/seconder).
      if (axios.isAxiosError<ApiErrorResponse>(error) && error.response?.status === 422) {
        const fieldErrors = error.response.data.errors ?? {}
        Object.entries(fieldErrors).forEach(([field, messages]) => {
          const message = messages[0]
          if (!message) {
            return
          }

          if (field.startsWith('new_department')) {
            form.setError('department_id', { type: 'server', message })
            return
          }
          if (field.startsWith('new_position')) {
            form.setError('position_id', { type: 'server', message })
            return
          }
          if (field.startsWith('new_hospitals')) {
            form.setError('hospital_ids', { type: 'server', message })
            return
          }

          form.setError(field as FieldPath<SignupFormValues>, {
            type: 'server',
            message,
          })
        })
      }
    }
  })

  const hospitalOptions = useMemo(
    () => [
      ...(formData?.hospitals ?? []).map((hospital) => ({ value: hospital.id, label: hospital.name })),
      ...pendingHospitals.map((hospital) => ({ value: hospital.tempId, label: hospital.name })),
    ],
    [formData?.hospitals, pendingHospitals],
  )

  const positionOptions = useMemo(() => {
    const options = (formData?.positions ?? []).map((position) => ({
      value: position.id,
      label: position.name,
    }))
    if (pendingPosition) {
      options.push({ value: PENDING_POSITION_ID, label: pendingPosition.name })
    }
    return options
  }, [formData?.positions, pendingPosition])

  const salutationOptions = useMemo(
    () => REVIEWER_SALUTATION_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  )

  const saveNewDepartment = departmentCreateForm.handleSubmit((values) => {
    const name = values.name.trim()
    const names = collectDepartmentNames(removeDepartmentFromTree(departments, PENDING_DEPARTMENT_ID))
    if (names.some((existing) => namesMatch(existing, name))) {
      departmentCreateForm.setError('name', { type: 'manual', message: 'The name has already been taken.' })
      return
    }

    const node: Department = {
      id: PENDING_DEPARTMENT_ID,
      name,
      parent_id: values.parent_id || null,
      is_active: values.is_active,
      created_at: null,
      deleted_at: null,
      children: [],
    }
    const withoutPending = removeDepartmentFromTree(departments, PENDING_DEPARTMENT_ID)
    setDepartments(insertDepartmentInTree(withoutPending, node))
    setPendingDepartment({
      name,
      parent_id: values.parent_id || null,
      is_active: values.is_active,
    })
    form.setValue('department_id', PENDING_DEPARTMENT_ID, { shouldValidate: true })
    closeCreateModal()
  })

  const saveNewPosition = positionCreateForm.handleSubmit((values) => {
    const name = values.name.trim()
    const taken = positionOptions.some(
      (option) => option.value !== PENDING_POSITION_ID && namesMatch(option.label, name),
    )
    if (taken) {
      positionCreateForm.setError('name', { type: 'manual', message: 'The name has already been taken.' })
      return
    }

    setPendingPosition({ name, is_active: values.is_active })
    form.setValue('position_id', PENDING_POSITION_ID, { shouldValidate: true })
    closeCreateModal()
  })

  const saveNewHospital = hospitalCreateForm.handleSubmit((values) => {
    const name = values.name.trim()
    const taken = hospitalOptions.some((option) => namesMatch(option.label, name))
    if (taken) {
      hospitalCreateForm.setError('name', { type: 'manual', message: 'The name has already been taken.' })
      return
    }

    const tempId = `pending:hospital:${crypto.randomUUID()}`
    const hospital: PendingHospital = {
      tempId,
      name,
      address: values.address?.trim() || undefined,
      is_active: values.is_active,
    }
    setPendingHospitals((current) => [...current, hospital])
    form.setValue('hospital_ids', [...selectedHospitalIds, tempId], { shouldValidate: true })
    closeCreateModal()
  })

  if (submitted) {
    return (
      <AuthLayout
        title="Application submitted"
        description="Thank you for registering. Login credentials have been emailed. You can sign in now — full access opens after approval."
        footer={
          <p className="text-center text-sm text-ink-600">
            <Link to="/login" className="font-semibold text-green-600 hover:text-green-700">
              Back to Login
            </Link>
          </p>
        }
      >
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          Check your email for a temporary password, then sign in. Until approval you will only see an account-status page.
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Sign Up"
      description="Submit your professional profile. You will receive login credentials by email right away; full access requires admin approval."
      footer={
        <p className="text-center text-sm text-ink-600">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-green-600 hover:text-green-700">
            Back to Login
          </Link>
        </p>
      }
    >
      <form className="max-h-[70vh] space-y-4 overflow-y-auto pr-1" onSubmit={onSubmit}>
        <FormRequiredLegend />
        <div>
          <FieldLabel required>Salutation</FieldLabel>
          <Controller
            control={form.control}
            name="salutation"
            render={({ field }) => (
              <SelectPicker options={salutationOptions} value={field.value} onChange={field.onChange} />
            )}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel required>First name</FieldLabel>
            <Input {...form.register('first_name')} />
            {form.formState.errors.first_name && (
              <p className="mt-1 text-xs text-danger">{form.formState.errors.first_name.message}</p>
            )}
          </div>
          <div>
            <FieldLabel required>Surname</FieldLabel>
            <Input {...form.register('surname')} />
            {form.formState.errors.surname && (
              <p className="mt-1 text-xs text-danger">{form.formState.errors.surname.message}</p>
            )}
          </div>
        </div>

        <div>
          <FieldLabel required>Email</FieldLabel>
          <Input type="email" {...form.register('email')} />
          {form.formState.errors.email && (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div>
          <FieldLabel required>Registration number</FieldLabel>
          <Input {...form.register('registration_number')} placeholder="e.g. IMC-12345" />
          {form.formState.errors.registration_number && (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.registration_number.message}</p>
          )}
        </div>

        <div>
          <FieldLabel optional>Phone number</FieldLabel>
          <Input {...form.register('phone_number')} />
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <FieldLabel required>Department</FieldLabel>
            <button
              type="button"
              className="text-xs font-semibold text-green-600 hover:text-green-700"
              onClick={() => openCreateModal('department')}
            >
              Add New
            </button>
          </div>
          <Controller
            control={form.control}
            name="department_id"
            render={({ field }) => (
              <DepartmentPicker
                tree={departments}
                value={field.value}
                onChange={field.onChange}
                required
                placeholder="Select department"
              />
            )}
          />
          {form.formState.errors.department_id && (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.department_id.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <FieldLabel required>Position</FieldLabel>
            <button
              type="button"
              className="text-xs font-semibold text-green-600 hover:text-green-700"
              onClick={() => openCreateModal('position')}
            >
              Add New
            </button>
          </div>
          <Controller
            control={form.control}
            name="position_id"
            render={({ field }) => (
              <SelectPicker
                options={positionOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select position"
              />
            )}
          />
          {form.formState.errors.position_id && (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.position_id.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <FieldLabel required>Hospitals</FieldLabel>
            <button
              type="button"
              className="text-xs font-semibold text-green-600 hover:text-green-700"
              onClick={() => openCreateModal('hospital')}
            >
              Add New
            </button>
          </div>
          <Controller
            control={form.control}
            name="hospital_ids"
            render={({ field }) => (
              <MultiSelectPicker
                options={hospitalOptions}
                values={field.value}
                onChange={field.onChange}
                placeholder="Select hospitals"
              />
            )}
          />
          {form.formState.errors.hospital_ids && (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.hospital_ids.message}</p>
          )}
        </div>

        <div>
          <FieldLabel required>Documents (1 to 5)</FieldLabel>
          <Input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/jpg,application/pdf"
            disabled={selectedDocuments.length >= MAX_SIGNUP_DOCUMENTS}
            onChange={(event) => {
              const picked = Array.from(event.target.files ?? [])
              if (picked.length === 0) {
                return
              }

              const existingKeys = new Set(selectedDocuments.map(fileKey))
              const merged = [...selectedDocuments]
              for (const file of picked) {
                if (merged.length >= MAX_SIGNUP_DOCUMENTS) {
                  break
                }
                if (existingKeys.has(fileKey(file))) {
                  continue
                }
                merged.push(file)
                existingKeys.add(fileKey(file))
              }

              form.setValue('documents', merged, { shouldValidate: true })
              event.target.value = ''
            }}
          />
          <p className="mt-1 text-xs text-ink-500">
            Upload at least one document/image. Maximum {MAX_SIGNUP_DOCUMENTS} files.
            {selectedDocuments.length > 0
              ? ` Selected: ${selectedDocuments.length}/${MAX_SIGNUP_DOCUMENTS}.`
              : ''}
          </p>
          {selectedDocuments.length > 0 && (
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {selectedDocuments.map((file, index) => (
                <li
                  key={`${fileKey(file)}-${index}`}
                  className="relative rounded-md border border-parch-100 bg-parch-50 px-3 py-2.5 pr-9"
                >
                  <p className="truncate text-sm font-medium text-ink-800" title={file.name}>
                    {file.name}
                  </p>
                  <p className="text-xs text-ink-500">{Math.round(file.size / 1024)} KB</p>
                  <button
                    type="button"
                    className="absolute top-1.5 right-1.5 grid h-6 w-6 place-items-center rounded text-ink-400 transition-colors hover:bg-white hover:text-danger"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => {
                      form.setValue(
                        'documents',
                        selectedDocuments.filter((_, fileIndex) => fileIndex !== index),
                        { shouldValidate: true },
                      )
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {form.formState.errors.documents && (
            <p className="mt-1 text-xs text-danger">{form.formState.errors.documents.message as string}</p>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <p className="section-label mb-1">Doctor references</p>
            <p className="text-xs text-ink-500">
              Provide two different doctors who can vouch for your application. Name, phone
              number, and email must each be unique across both people.
            </p>
          </div>

          <fieldset className="rounded-md border border-parch-100 bg-parch-50/60 p-4">
            <legend className="px-1 text-sm font-semibold text-ink-800">1. Proposer</legend>
            <p className="mb-3 text-xs text-ink-500">
              First reference — the doctor who proposes your membership.
            </p>
            <div className="space-y-3">
              <div>
                <FieldLabel required>Name</FieldLabel>
                <Input {...form.register('proposer_name')} autoComplete="name" />
                {form.formState.errors.proposer_name && (
                  <p className="mt-1 text-xs text-danger">{form.formState.errors.proposer_name.message}</p>
                )}
              </div>
              <div>
                <FieldLabel required>Phone number</FieldLabel>
                <Input {...form.register('proposer_phone_number')} autoComplete="tel" />
                {form.formState.errors.proposer_phone_number && (
                  <p className="mt-1 text-xs text-danger">
                    {form.formState.errors.proposer_phone_number.message}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel required>Email</FieldLabel>
                <Input type="email" {...form.register('proposer_email')} autoComplete="email" />
                {form.formState.errors.proposer_email && (
                  <p className="mt-1 text-xs text-danger">{form.formState.errors.proposer_email.message}</p>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset className="rounded-md border border-parch-100 bg-parch-50/60 p-4">
            <legend className="px-1 text-sm font-semibold text-ink-800">2. Seconder</legend>
            <p className="mb-3 text-xs text-ink-500">
              Second reference — a different doctor who seconds your membership. Must not match the
              proposer&apos;s name, phone, or email.
            </p>
            <div className="space-y-3">
              <div>
                <FieldLabel required>Name</FieldLabel>
                <Input {...form.register('seconder_name')} autoComplete="name" />
                {form.formState.errors.seconder_name && (
                  <p className="mt-1 text-xs text-danger">{form.formState.errors.seconder_name.message}</p>
                )}
              </div>
              <div>
                <FieldLabel required>Phone number</FieldLabel>
                <Input {...form.register('seconder_phone_number')} autoComplete="tel" />
                {form.formState.errors.seconder_phone_number && (
                  <p className="mt-1 text-xs text-danger">
                    {form.formState.errors.seconder_phone_number.message}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel required>Email</FieldLabel>
                <Input type="email" {...form.register('seconder_email')} autoComplete="email" />
                {form.formState.errors.seconder_email && (
                  <p className="mt-1 text-xs text-danger">{form.formState.errors.seconder_email.message}</p>
                )}
              </div>
            </div>
          </fieldset>
        </div>

        <div className="space-y-2">
          <Controller
            control={form.control}
            name="terms_accepted"
            render={({ field }) => (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-parch-300"
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
                <span>
                  I accept the terms and conditions
                  <RequiredMark />
                </span>
              </label>
            )}
          />
          {form.formState.errors.terms_accepted && (
            <p className="text-xs text-danger">{form.formState.errors.terms_accepted.message}</p>
          )}

          <Controller
            control={form.control}
            name="privacy_accepted"
            render={({ field }) => (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-parch-300"
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
                <span>
                  I accept the privacy policy
                  <RequiredMark />
                </span>
              </label>
            )}
          />
          {form.formState.errors.privacy_accepted && (
            <p className="text-xs text-danger">{form.formState.errors.privacy_accepted.message}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={form.formState.isSubmitting || !formData}>
          {form.formState.isSubmitting ? 'Submitting…' : 'Submit Application'}
        </Button>
      </form>

      <SignupCreateModal
        open={createModal === 'department'}
        title="Create Department"
        description="Choose a parent to nest under, or leave as top-level."
        onClose={closeCreateModal}
      >
        <form className="mt-6 space-y-4" onSubmit={saveNewDepartment}>
          <FormRequiredLegend />
          <div>
            <FieldLabel required>Name</FieldLabel>
            <Input {...departmentCreateForm.register('name')} />
            {departmentCreateForm.formState.errors.name && (
              <p className="mt-1 text-xs text-danger">{departmentCreateForm.formState.errors.name.message}</p>
            )}
          </div>
          <div>
            <FieldLabel optional>Parent department</FieldLabel>
            <Controller
              control={departmentCreateForm.control}
              name="parent_id"
              render={({ field }) => (
                <DepartmentParentPicker
                  tree={formData?.departments ?? []}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              )}
            />
            <p className="mt-1 text-xs text-ink-500">
              Hover a department to expand its sub-departments, then click to select.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...departmentCreateForm.register('is_active')} />
            Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button type="submit">Create Department</Button>
          </div>
        </form>
      </SignupCreateModal>

      <SignupCreateModal
        open={createModal === 'position'}
        title="Create Position"
        onClose={closeCreateModal}
      >
        <form className="mt-6 space-y-4" onSubmit={saveNewPosition}>
          <FormRequiredLegend />
          <div>
            <FieldLabel required>Name</FieldLabel>
            <Input {...positionCreateForm.register('name')} />
            {positionCreateForm.formState.errors.name && (
              <p className="mt-1 text-xs text-danger">{positionCreateForm.formState.errors.name.message}</p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...positionCreateForm.register('is_active')} />
            Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button type="submit">Create Position</Button>
          </div>
        </form>
      </SignupCreateModal>

      <SignupCreateModal
        open={createModal === 'hospital'}
        title="Create Hospital"
        onClose={closeCreateModal}
      >
        <form className="mt-6 space-y-4" onSubmit={saveNewHospital}>
          <FormRequiredLegend />
          <div>
            <FieldLabel required>Name</FieldLabel>
            <Input {...hospitalCreateForm.register('name')} />
            {hospitalCreateForm.formState.errors.name && (
              <p className="mt-1 text-xs text-danger">{hospitalCreateForm.formState.errors.name.message}</p>
            )}
          </div>
          <div>
            <FieldLabel optional>Address</FieldLabel>
            <Input {...hospitalCreateForm.register('address')} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...hospitalCreateForm.register('is_active')} />
            Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button type="submit">Create Hospital</Button>
          </div>
        </form>
      </SignupCreateModal>
    </AuthLayout>
  )
}

function SignupCreateModal({
  open,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  children: ReactNode
}) {
  if (!open) {
    return null
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-card"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold">{title}</h2>
            {description ? <p className="mt-1 text-sm text-ink-600">{description}</p> : null}
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-ink-500 hover:bg-parch-50 hover:text-ink-900"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
